import { useState, useEffect, useRef, useCallback } from 'react';
import { Download, Check, Loader2, Trash2, HardDrive } from 'lucide-react';
import { Button } from './ui/button';
import { useOffline } from '../hooks/useOffline';
import { getAudioUrl } from '../lib/api';
import { toast } from 'sonner';

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const idx = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / Math.pow(1024, idx);
  const precision = value >= 100 || idx <= 1 ? 0 : 1;
  return `${value.toFixed(precision)} ${units[idx]}`;
};

/**
 * Button that downloads all audio files of a surah's excerpts into the
 * service-worker audio cache for fully reliable offline playback.
 * Shows a storage usage indicator under the button.
 */
export const OfflineDownloadButton = ({ excerpts }) => {
  const { isServiceWorkerReady, cacheAudioBatch, queryAudioCache, clearAudioCache } = useOffline();
  const [isDownloading, setIsDownloading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [allCached, setAllCached] = useState(false);
  const [storage, setStorage] = useState(null); // {usage, quota}
  const mountedRef = useRef(true);

  const audioUrls = (excerpts || [])
    .filter((e) => e && e.audio_file_id)
    .map((e) => getAudioUrl(e.audio_file_id));

  const refreshStorage = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.storage || !navigator.storage.estimate) {
      setStorage(null);
      return;
    }
    try {
      const est = await navigator.storage.estimate();
      if (!mountedRef.current) return;
      setStorage({
        usage: est.usage || 0,
        quota: est.quota || 0,
      });
    } catch (e) {
      setStorage(null);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Determine current cache state when excerpts/SW are ready
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!isServiceWorkerReady || audioUrls.length === 0) {
        setAllCached(false);
        return;
      }
      const map = await queryAudioCache(audioUrls);
      if (cancelled) return;
      const cachedAll = audioUrls.every((u) => map[u]);
      setAllCached(cachedAll);
    };
    check();
    refreshStorage();
    return () => { cancelled = true; };
  }, [isServiceWorkerReady, audioUrls.length, queryAudioCache, refreshStorage]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isServiceWorkerReady || audioUrls.length === 0) return null;

  const handleDownload = async () => {
    setIsDownloading(true);
    setProgress({ done: 0, total: audioUrls.length });
    try {
      const res = await cacheAudioBatch(audioUrls, (p) => {
        if (mountedRef.current) setProgress(p);
      });
      if (res.success === audioUrls.length) {
        toast.success('تم تحميل جميع المقاطع للاستماع بدون اتصال');
        setAllCached(true);
      } else if (res.success > 0) {
        toast.warning(`تم تحميل ${res.success} من ${audioUrls.length} مقاطع`);
        setAllCached(false);
      } else {
        toast.error('تعذر تحميل المقاطع');
      }
    } catch (e) {
      toast.error('خطأ أثناء التحميل');
    } finally {
      if (mountedRef.current) {
        setIsDownloading(false);
        refreshStorage();
      }
    }
  };

  const handleClear = async () => {
    const ok = await clearAudioCache();
    if (ok) {
      toast.success('تم مسح ذاكرة التخزين المؤقت للصوت');
      setAllCached(false);
    } else {
      toast.error('تعذر مسح الذاكرة');
    }
    refreshStorage();
  };

  const renderStorageIndicator = () => {
    if (!storage || storage.quota <= 0) return null;
    const percent = Math.min(100, Math.max(0, (storage.usage / storage.quota) * 100));
    return (
      <div
        className="mt-2 flex flex-col items-end gap-1 text-xs text-muted-foreground"
        dir="rtl"
        data-testid="storage-indicator"
      >
        <div className="flex items-center gap-1.5">
          <HardDrive className="h-3.5 w-3.5 opacity-70" />
          <span data-testid="storage-text">
            {formatBytes(storage.usage)} / {formatBytes(storage.quota)}
            <span className="opacity-60"> ({percent.toFixed(percent < 1 ? 2 : 1)}%)</span>
          </span>
        </div>
        <div className="w-40 h-1 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full ${percent > 85 ? 'bg-red-500' : 'bg-primary/60'} transition-all`}
            style={{ width: `${percent}%` }}
            data-testid="storage-bar-fill"
          />
        </div>
      </div>
    );
  };

  const buttonEl =
    allCached && !isDownloading ? (
      <Button
        variant="outline"
        onClick={handleClear}
        className="rounded-full gap-2 shrink-0 border-green-500/50 text-green-500 hover:bg-green-500/10"
        data-testid="offline-clear-btn"
        title="حذف من التخزين بدون اتصال"
      >
        <Check className="h-5 w-5" />
        <span className="hidden sm:inline">محفوظ بدون اتصال</span>
        <Trash2 className="h-4 w-4 opacity-70" />
      </Button>
    ) : (
      <Button
        variant="outline"
        onClick={handleDownload}
        disabled={isDownloading}
        className="rounded-full gap-2 shrink-0"
        data-testid="offline-download-btn"
        title="حفظ للاستماع بدون اتصال"
      >
        {isDownloading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>{progress.done}/{progress.total}</span>
          </>
        ) : (
          <>
            <Download className="h-5 w-5" />
            <span className="hidden sm:inline">حفظ للاستماع بدون اتصال</span>
          </>
        )}
      </Button>
    );

  return (
    <div className="flex flex-col items-end">
      {buttonEl}
      {renderStorageIndicator()}
    </div>
  );
};
